import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ExternalLink, LogIn, RefreshCw, ChevronDown, ChevronRight, History, ArrowLeft } from 'lucide-react';
import {
  Button,
  Badge,
  InShellPanel,
  InShellPanelHeader,
  InShellPanelTitle,
  InShellPanelDescription,
  Separator,
} from '@staff-app/ui';
import type { CustomerResolveResult, CustomerInfo, CustomerInfoAuditEntry, CustomerNote } from '@staff-app/shared-types';
import {
  useStaffGetCustomerInfo,
  useStaffGetCustomerInfoAuditLog,
  useStaffGetCustomerNotes,
  useStaffAddCustomerNote,
  useStaffUpdateCustomerInfo,
  useStaffLoginAsCustomer,
} from '../hooks/useStaffCustomer';

const PAGE_SIZE = 5;

function Paginator({ page, total, onPrev, onNext }: { page: number; total: number; onPrev: () => void; onNext: () => void }) {
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between pt-1">
      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={onPrev} disabled={page === 0}>
        {'← Prev'}
      </Button>
      <span className="text-[10px] text-muted-foreground">{`${page + 1} / ${totalPages}`}</span>
      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={onNext} disabled={page >= totalPages - 1}>
        {'Next →'}
      </Button>
    </div>
  );
}

function AuditHistory({ entries }: { entries: CustomerInfoAuditEntry[] }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  if (!entries.length) return (
    <p className="py-2 text-center text-xs text-muted-foreground">{'No edit history yet.'}</p>
  );
  const pageEntries = entries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  return (
    <div className="flex flex-col gap-1">
      {pageEntries.map((entry) => {
        let diffs: [string, { from: unknown; to: unknown }][] = [];
        try { diffs = Object.entries(JSON.parse(entry.changes ?? '{}')); } catch { /* ignore */ }
        const isOpen = expanded === entry._id;
        const date = new Date(entry.changedAt);
        return (
          <div key={entry._id} className="rounded border bg-muted/40 text-xs">
            <button
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent/30"
              onClick={() => setExpanded(isOpen ? null : entry._id)}
            >
              {isOpen ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
              <span className="font-medium truncate">{entry.staffEmail}</span>
              <span className="ml-auto shrink-0 text-muted-foreground">
                {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </button>
            {isOpen && (
              <div className="border-t px-3 py-2 font-mono text-[11px] space-y-0.5">
                {diffs.length ? diffs.map(([key, { from, to }]) => (
                  <div key={key}>
                    <span className="text-foreground">{key}</span>
                    {': '}
                    <span className="text-destructive">{JSON.stringify(from)}</span>
                    {' → '}
                    <span className="text-green-600 dark:text-green-400">{JSON.stringify(to)}</span>
                  </div>
                )) : <div className="text-muted-foreground">{'(no detectable changes)'}</div>}
              </div>
            )}
          </div>
        );
      })}
      <Paginator
        page={page}
        total={entries.length}
        onPrev={() => setPage(p => p - 1)}
        onNext={() => setPage(p => p + 1)}
      />
    </div>
  );
}

function NoteCompose({ value, onChange, saving, onSave }: {
  value: string;
  onChange: (v: string) => void;
  saving: boolean;
  onSave: () => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <textarea
        className="w-full resize-none rounded border bg-muted px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        rows={3}
        placeholder="Add a note for other agents…"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') onSave(); }}
        spellCheck={false}
      />
      {value.trim() && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">{'Ctrl+Enter to save'}</span>
          <Button size="sm" className="h-6 px-2 text-xs" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Note'}
          </Button>
        </div>
      )}
    </div>
  );
}

function NotesList({ notes }: { notes: CustomerNote[] }) {
  const [page, setPage] = useState(0);
  if (!notes.length) return (
    <p className="py-1 text-center text-xs text-muted-foreground">{'No notes yet.'}</p>
  );
  const pageNotes = notes.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  return (
    <div className="flex flex-col gap-1.5">
      {pageNotes.map((note) => {
        const date = new Date(note.createdAt);
        return (
          <div key={note._id} className="rounded border bg-muted/40 px-3 py-2 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium">{note.staffEmail}</span>
              <span className="text-[10px] text-muted-foreground">
                {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            <p className="whitespace-pre-wrap text-muted-foreground">{note.text}</p>
          </div>
        );
      })}
      <Paginator
        page={page}
        total={notes.length}
        onPrev={() => setPage(p => p - 1)}
        onNext={() => setPage(p => p + 1)}
      />
    </div>
  );
}

function mergeInfo(info: CustomerInfo): string {
  try {
    return JSON.stringify(
      {
        payment: JSON.parse(info.payment),
        limitation: JSON.parse(info.limitation),
        feature: JSON.parse(info.feature),
      },
      null,
      2,
    );
  } catch {
    return JSON.stringify({ payment: info.payment, limitation: info.limitation, feature: info.feature }, null, 2);
  }
}

interface CustomerPanelProps {
  open: boolean;
  onClose: () => void;
  resolved: CustomerResolveResult | null;
}

export function CustomerPanel({ open, onClose, resolved }: CustomerPanelProps) {
  const accountId = resolved?.accountId ?? null;
  const { data: infoData, loading: infoLoading, refetch: refetchInfo } = useStaffGetCustomerInfo(accountId);
  const { data: auditData, refetch: refetchAudit } = useStaffGetCustomerInfoAuditLog(accountId);
  const auditEntries = auditData?.staffGetCustomerInfoAuditLog ?? [];
  const { data: notesData, refetch: refetchNotes } = useStaffGetCustomerNotes(accountId);
  const notes = notesData?.staffGetCustomerNotes ?? [];
  const [addNoteMutation, { loading: addingNote }] = useStaffAddCustomerNote();
  const [updateInfo] = useStaffUpdateCustomerInfo();
  const [loginMutation, { loading: loginLoading }] = useStaffLoginAsCustomer();

  const info = infoData?.staffGetCustomerInfo;

  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [noteText, setNoteText] = useState('');


  useEffect(() => {
    if (info) {
      setDraft(mergeInfo(info));
      setError(null);
    }
  }, [info]);

  const handleLogin = async () => {
    if (!accountId) return;
    try {
      const result = await loginMutation({ variables: { accountId } });
      const appUrl = result.data?.staffLoginAsCustomer?.appUrl;
      if (appUrl) window.open(appUrl, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      toast.error('Login failed', { description: (e as Error)?.message ?? 'Unknown error' });
    }
  };

  const handleRefresh = async () => {
    const result = await refetchInfo();
    const fresh = result.data?.staffGetCustomerInfo;
    if (fresh) {
      setDraft(mergeInfo(fresh));
      setError(null);
    }
  };

  const handleSave = async () => {
    let parsed: { payment?: unknown; limitation?: unknown; feature?: unknown };
    try {
      parsed = JSON.parse(draft);
    } catch {
      setError('Invalid JSON');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateInfo({
        variables: {
          accountId: accountId ?? '',
          patch: {
            payment: JSON.stringify(parsed.payment ?? {}),
            limitation: JSON.stringify(parsed.limitation ?? {}),
            feature: JSON.stringify(parsed.feature ?? {}),
          },
        },
      });
      refetchAudit();
    } catch (e: unknown) {
      setError((e as Error)?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const teams = resolved?.teams ?? [];
  const teamLabel =
    teams.length === 0
      ? 'Default Team'
      : teams.map((t) => (t.isCurrent ? `${t.teamName} (active)` : t.teamName)).join(', ');

  const stripeId = (() => {
    try { return info ? ((JSON.parse(info.payment) as Record<string, unknown>)?.stripe_id as string) ?? null : null; }
    catch { return null; }
  })();

  return (
    <InShellPanel
      open={open}
      onOpenChange={(next) => !next && onClose()}
      side="right"
      data-testid="customer-panel"
      className="sm:w-[48vw] sm:max-w-[48vw]"
    >
      {resolved && (
        <div className="flex flex-col gap-5 pt-2 h-full">
          <InShellPanelHeader>
            <InShellPanelTitle>
              {resolved.companyName ?? resolved.accountId ?? 'Customer'}
            </InShellPanelTitle>
            <InShellPanelDescription className="space-y-0.5">
              <span className="block">
                <span className="text-muted-foreground">{'Account: '}</span>
                <span className="font-mono text-xs">{resolved.accountId}</span>
                {resolved.plan && (
                  <Badge variant="secondary" className="ml-2 text-[10px]">
                    {resolved.plan}
                  </Badge>
                )}
              </span>
              {resolved.ownerEmail && (
                <span className="block">
                  <span className="text-muted-foreground">{'Owner: '}</span>
                  <span className="text-xs">{resolved.ownerEmail}</span>
                </span>
              )}
              {!resolved.inputIsOwner && (
                <span className="block">
                  <span className="text-muted-foreground">{'Searched: '}</span>
                  <span className="text-xs">{resolved.inputEmail}</span>
                </span>
              )}
              {teams.length > 0 && (
                <span className="block">
                  <span className="text-muted-foreground">{'Teams: '}</span>
                  <span className="text-xs">{teamLabel}</span>
                </span>
              )}
            </InShellPanelDescription>
          </InShellPanelHeader>

          <div className="flex gap-2">
            {resolved.ownerResolvable ? (
              <Button
                className="flex-1 gap-2"
                onClick={handleLogin}
                disabled={loginLoading}
              >
                <LogIn className="h-4 w-4" />
                {loginLoading ? 'Opening…' : 'Login as Customer'}
                <ExternalLink className="h-3 w-3 opacity-60" />
              </Button>
            ) : (
              <div className="flex-1 rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {'Owner not resolvable — investigate accountId manually.'}
                <div className="mt-1 font-mono">{resolved.accountId}</div>
              </div>
            )}

            {stripeId && (
              <Button
                variant="outline"
                className="flex-1 gap-2"
                onClick={() => window.open(`https://dashboard.stripe.com/customers/${stripeId}`, '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink className="h-4 w-4" />
                {'Stripe'}
              </Button>
            )}
          </div>

          <Separator />

          {infoLoading && !info ? (
            <p className="text-center text-xs text-muted-foreground">{'Loading company data…'}</p>
          ) : info ? (
            showHistory ? (
              <div className="flex flex-col flex-1 min-h-0 gap-3 overflow-y-auto">
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={() => setShowHistory(false)}>
                    <ArrowLeft className="h-3 w-3" />
                    {'Back'}
                  </Button>
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {'History'}
                  </span>
                </div>

                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {'Edit History'}
                </span>
                <AuditHistory entries={auditEntries} />

                <Separator />

                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {'Notes'}
                </span>
                <NoteCompose
                  value={noteText}
                  onChange={setNoteText}
                  saving={addingNote}
                  onSave={async () => {
                    if (!accountId || !noteText.trim()) return;
                    await addNoteMutation({ variables: { accountId, text: noteText.trim() } });
                    setNoteText('');
                    refetchNotes();
                  }}
                />
                <NotesList notes={notes} />
              </div>
            ) : (
              <div className="flex flex-col flex-1 min-h-0 gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {'Info'}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 gap-1 px-2 text-xs"
                      onClick={() => setShowHistory(true)}
                    >
                      <History className="h-3 w-3" />
                      {'History'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={handleRefresh}
                      disabled={infoLoading}
                    >
                      <RefreshCw className={`h-3 w-3 ${infoLoading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
                {error && <p className="text-xs text-destructive">{error}</p>}
                <textarea
                  className="flex-1 min-h-0 w-full resize-none rounded border bg-muted px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                  value={draft}
                  onChange={(e) => { setDraft(e.target.value); setError(null); }}
                  spellCheck={false}
                />
              </div>
            )
          ) : null}
        </div>
      )}
    </InShellPanel>
  );
}
